/**
 * Addresses: list, add, edit and remove. Self mode uses /me/addresses, admin mode
 * /{memberId}/addresses. Removing an address only deletes the address row when no other member
 * (for example a spouse) still uses it.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { IconPlus, IconEdit, IconTrash, IconMapPin, IconAlertCircle } from '@tabler/icons-react';
import { Card, Stack, Group, Button, TextInput, SimpleGrid, Text, Alert, Modal, Skeleton, Tooltip, ActionIcon, ThemeIcon, EmptyState } from '@/components/ui';
import { toast } from '@/components/ui/providers/AlertProvider';
import { modal } from '@/components/ui/providers/ModalsProvider';
import type { MemberAddress } from '@actschurch/ccms-sdk';
import type { MemberTabProps } from '../types';
import type { MemberAddressFields } from '../useMemberProfileApi';
import { errorMessage } from '../memberFormat';

const EMPTY: Required<MemberAddressFields> = { addressLine1: '', addressLine2: '', suburb: '', city: '', code: '', province: '', country: 'South Africa' };

function addressLines(a: MemberAddress): string[] {
  return [
    a.addressLine1,
    a.addressLine2,
    [a.suburb, a.city].filter(Boolean).join(', '),
    [a.province, a.code].filter(Boolean).join(' '),
    a.country,
  ].map((s) => (s ?? '').trim()).filter(Boolean);
}

export function AddressesTab({ api, perms }: MemberTabProps) {
  const queryClient = useQueryClient();
  const addresses = useQuery({ queryKey: api.keys.addresses, queryFn: () => api.addresses.list() });
  const [editing, setEditing] = useState<{ id: number | null } | null>(null);
  const [form, setForm] = useState<Required<MemberAddressFields>>(EMPTY);

  const refresh = () => queryClient.invalidateQueries({ queryKey: api.keys.addresses });

  const save = useMutation({
    mutationFn: async () => {
      const body: MemberAddressFields = Object.fromEntries(
        Object.entries(form).map(([k, v]) => [k, v.trim()]),
      );
      if (editing?.id) return api.addresses.update(editing.id, body);
      return api.addresses.add(body);
    },
    onSuccess: () => {
      toast.success('Saved', editing?.id ? 'Address updated.' : 'Address added.');
      setEditing(null);
      refresh();
    },
    onError: (e) => toast.error('Could not save the address', errorMessage(e)),
  });

  const remove = useMutation({
    mutationFn: (id: number) => api.addresses.remove(id),
    onSuccess: () => { toast.success('Removed', 'Address removed.'); refresh(); },
    onError: (e) => toast.error('Could not remove the address', errorMessage(e)),
  });

  const openAdd = () => { setForm(EMPTY); save.reset(); setEditing({ id: null }); };
  const openEdit = (a: MemberAddress) => {
    setForm({
      addressLine1: a.addressLine1 ?? '', addressLine2: a.addressLine2 ?? '', suburb: a.suburb ?? '',
      city: a.city ?? '', code: a.code ?? '', province: a.province ?? '', country: a.country ?? '',
    });
    save.reset();
    setEditing({ id: a.addressID });
  };

  const list = addresses.data ?? [];
  const canSave = form.addressLine1.trim().length > 0 && (form.city.trim().length > 0 || form.suburb.trim().length > 0);

  return (
    <Card withBorder padding="lg" radius="md">
      <Stack gap="md">
        <Group justify="space-between" align="flex-start">
          <div>
            <Text fw={600}>Addresses</Text>
            <Text size="sm" c="dimmed">Home and postal addresses.</Text>
          </div>
          {perms.edit && <Button size="sm" leftSection={<IconPlus size={16} />} onClick={openAdd}>Add address</Button>}
        </Group>

        {addresses.isLoading && <Stack gap="sm"><Skeleton height={72} /><Skeleton height={72} /></Stack>}
        {addresses.isError && (
          <Alert color="red" icon={<IconAlertCircle size={16} />} title="Could not load addresses">{errorMessage(addresses.error)}</Alert>
        )}
        {!addresses.isLoading && !addresses.isError && list.length === 0 && (
          <EmptyState size="sm" icon={<IconMapPin size={32} />} title="No addresses yet"
            description={perms.edit ? 'Add a home address so the church knows where to find you.' : 'No address has been recorded.'}
            actionLabel={perms.edit ? 'Add address' : undefined} onAction={perms.edit ? openAdd : undefined} />
        )}

        <SimpleGrid cols={{ base: 1, md: 2 }}>
          {list.map((a) => (
            <Card key={a.addressID} withBorder padding="md" radius="md">
              <Group justify="space-between" align="flex-start" wrap="nowrap">
                <Group gap="sm" align="flex-start" wrap="nowrap" style={{ minWidth: 0 }}>
                  <ThemeIcon variant="light" color="blue" size="lg"><IconMapPin size={18} /></ThemeIcon>
                  <Stack gap={0} style={{ minWidth: 0 }}>
                    {addressLines(a).map((line, i) => (
                      <Text key={i} size="sm" fw={i === 0 ? 600 : undefined}>{line}</Text>
                    ))}
                  </Stack>
                </Group>
                {perms.edit && (
                  <Group gap={4} wrap="nowrap">
                    <Tooltip label="Edit"><ActionIcon variant="subtle" onClick={() => openEdit(a)} aria-label="Edit address"><IconEdit size={16} /></ActionIcon></Tooltip>
                    <Tooltip label="Remove">
                      <ActionIcon variant="subtle" color="red" aria-label="Remove address"
                        onClick={() => modal.confirm({
                          title: 'Remove this address?',
                          body: 'The address is removed from this profile. Anyone else at the same address keeps it.',
                          confirmLabel: 'Remove',
                          confirmColor: 'red',
                          onConfirm: () => remove.mutate(a.addressID),
                        })}>
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Tooltip>
                  </Group>
                )}
              </Group>
            </Card>
          ))}
        </SimpleGrid>
      </Stack>

      <Modal opened={!!editing} onClose={() => setEditing(null)} title={editing?.id ? 'Edit address' : 'Add address'} size="lg" centered>
        <Stack gap="md">
          <TextInput label="Street address" required value={form.addressLine1} onChange={(e) => setForm({ ...form, addressLine1: e.currentTarget.value })} />
          <TextInput label="Address line 2" placeholder="Complex, unit or building (optional)" value={form.addressLine2} onChange={(e) => setForm({ ...form, addressLine2: e.currentTarget.value })} />
          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            <TextInput label="Suburb" value={form.suburb} onChange={(e) => setForm({ ...form, suburb: e.currentTarget.value })} />
            <TextInput label="City or town" value={form.city} onChange={(e) => setForm({ ...form, city: e.currentTarget.value })} />
            <TextInput label="Province" value={form.province} onChange={(e) => setForm({ ...form, province: e.currentTarget.value })} />
            <TextInput label="Postal code" value={form.code} onChange={(e) => setForm({ ...form, code: e.currentTarget.value })} />
          </SimpleGrid>
          <TextInput label="Country" value={form.country} onChange={(e) => setForm({ ...form, country: e.currentTarget.value })} />
          {save.isError && <Alert color="red" icon={<IconAlertCircle size={16} />}>{errorMessage(save.error)}</Alert>}
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setEditing(null)}>Cancel</Button>
            <Button loading={save.isPending} disabled={!canSave} onClick={() => save.mutate()}>Save address</Button>
          </Group>
        </Stack>
      </Modal>
    </Card>
  );
}
